import {
  Flex,
  Text,
  Spinner,
  DataTable,
  FormattedRelativeTime,
  type DataTableFeatures,
} from '@customerio/pluma-components/react';
import { createColumnHelper } from '@tanstack/table-core';
import type { ObjectModel } from '@customerio/hydra-ui-api';
import type { SearchResult } from '@/services/auth/types';
import { hydraUiApi, useHydraUIAPIQuery } from '@/services/api';
import { isEmptyString } from '@/utils/string';

const PREVIEW_SIZE = 5;

type Props = {
  result: Extract<SearchResult, { kind: 'object_type' }>;
};

type ObjectPreviewRow = {
  id: ObjectModel['id'];
  displayName: unknown;
  attributes: ObjectModel['attributes'];
};

const columnHelper = createColumnHelper<DataTableFeatures, ObjectPreviewRow>();

function getDisplayName(object: ObjectModel): unknown {
  return (
    object.attributes['name'] ??
    object.attributes['object_id'] ??
    object.attributes['cio_object_id'] ??
    object.identifiers['object_id'] ??
    object.identifiers['cio_object_id']
  );
}

function formatPreviewValue(value: unknown): string {
  if (value == null) return '—';
  if (typeof value === 'string') return isEmptyString(value) ? '—' : value;
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

type NameCellProps = {
  value: unknown;
};

function NameCell({ value }: NameCellProps) {
  const displayValue = formatPreviewValue(value);
  return (
    <Text size="xs" weight="medium" className="fs-mask" truncate title={displayValue}>
      {displayValue}
    </Text>
  );
}

type CreatedAtCellProps = {
  value: unknown;
};

function CreatedAtCell({ value }: CreatedAtCellProps) {
  const numericValue = Number(value);
  const date = Number.isFinite(numericValue) && numericValue > 0 ? numericValue : undefined;

  if (date == null) {
    return <Text size="xs">—</Text>;
  }

  return <FormattedRelativeTime date={date} size="xs" className="fs-mask" shouldUppercaseFirst={false} withTooltip />;
}

const columns = [
  columnHelper.accessor('displayName', {
    id: 'name',
    header: 'Name',
    cell: (info) => <NameCell value={info.getValue()} />,
  }),
  columnHelper.display({
    id: 'created_at',
    header: 'Created at',
    cell: (info) => <CreatedAtCell value={info.row.original.attributes['created_at']} />,
  }),
];

export function ObjectTypePreview({ result }: Props) {
  const { data, isLoading } = useHydraUIAPIQuery({
    queryKey: ['listObjects', result.other.env_id, result.other.id],
    queryFn: ({ signal }) =>
      hydraUiApi.objects.getObjectTypesObjects({
        params: {
          path: { environment_id: String(result.other.env_id), type_id: String(result.other.id) },
          query: { size: PREVIEW_SIZE },
        },
        signal,
      }),
  });

  if (isLoading) {
    return (
      <Flex alignItems="center" justifyContent="center" w="full" p="200">
        <Spinner color="information" />
      </Flex>
    );
  }

  const objects = data?.objects ?? [];

  if (objects.length === 0) {
    return (
      <Flex direction="column" alignItems="center" justifyContent="center" flex={1} w="full" p="200">
        <Text size="xs" color="subtle">
          No objects found
        </Text>
      </Flex>
    );
  }

  const tableData = objects.map((object) => ({
    id: object.id,
    displayName: getDisplayName(object),
    attributes: object.attributes,
  }));

  return <DataTable caption="Object list" data={tableData} columns={columns} />;
}
